/**
 * In-app "where did I come from" per history entry — lets Back return to the
 * page the user was actually on (e.g. Leads → Add quote → Back = Leads, not
 * the Quotations list) instead of a hard-coded parent route.
 *
 * Keyed by react-router's `location.key`, so browser Back/Forward (POP) keeps
 * each entry's original origin. In-memory only: after a full reload there is
 * no origin and callers fall back to their static parent route.
 */
const previousByKey = new Map<string, string | null>()
let current: { key: string; url: string } | null = null

export type NavType = 'PUSH' | 'REPLACE' | 'POP'

/** Idempotent — safe to call during render (StrictMode double-render). */
export function recordLocation(key: string, url: string, type: NavType) {
  if (current?.key === key) return
  if (!previousByKey.has(key)) {
    if (type === 'PUSH') previousByKey.set(key, current?.url ?? null)
    // A replace inherits the replaced entry's origin (e.g. create → view).
    else if (type === 'REPLACE') previousByKey.set(key, current ? (previousByKey.get(current.key) ?? null) : null)
    else previousByKey.set(key, null)
  }
  current = { key, url }
}

/** The in-app URL this history entry was opened from, or null (deep link / reload). */
export function previousUrl(key: string): string | null {
  return previousByKey.get(key) ?? null
}

export function pathOf(url: string): string {
  return url.split(/[?#]/)[0] ?? url
}
